import { useCallback, useEffect, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

function storageKey(workflowId) {
  return `maintella:workflow-ai-editor:${workflowId}`;
}

const MAX_PERSISTED_MESSAGES = 30;

function capPersistedMessages(messages) {
  if (!Array.isArray(messages) || messages.length <= MAX_PERSISTED_MESSAGES) {
    return messages;
  }
  return messages.slice(-MAX_PERSISTED_MESSAGES);
}

export default function useWorkflowAiEditor({ workflowId, workflowSteps, onStepsApplied }) {
  const isMounted = useIsMounted();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [lastMeta, setLastMeta] = useState(null);

  useEffect(() => {
    if (!workflowId) {
      setMessages([]);
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey(workflowId));
      if (!raw) {
        setMessages([]);
        return;
      }
      const parsed = JSON.parse(raw);
      const persistedMessages = Array.isArray(parsed?.messages)
        ? parsed.messages.filter(
            (m) =>
              m &&
              (m.role === 'user' || m.role === 'assistant') &&
              typeof m.content === 'string'
          )
        : [];
      setMessages(persistedMessages);
    } catch (err) {
      if (!isMounted.current) return;
      console.warn('Failed to hydrate workflow AI editor from localStorage:', err);
      setMessages([]);
    }
  }, [workflowId, isMounted]);

  useEffect(() => {
    if (!workflowId) return;
    try {
      window.localStorage.setItem(
        storageKey(workflowId),
        JSON.stringify({
          messages: capPersistedMessages(messages),
          updatedAt: new Date().toISOString(),
        })
      );
    } catch (err) {
      if (!isMounted.current) return;
      console.warn('Failed to persist workflow AI editor state:', err);
    }
  }, [workflowId, messages, isMounted]);

  const clearPersistedState = useCallback(() => {
    if (!workflowId) return;
    try {
      window.localStorage.removeItem(storageKey(workflowId));
    } catch (err) {
      if (!isMounted.current) return;
      console.warn('Failed to clear workflow AI editor localStorage state:', err);
    }
  }, [workflowId, isMounted]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text?.trim();
      if (!trimmed || !workflowId) return;

      setLoading(true);
      setError('');
      setValidationErrors([]);
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/v1/admin/workflows/${workflowId}/ai/turn`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: trimmed,
              draft_workflow_steps: workflowSteps || [],
              messages,
            }),
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'AI request failed');
        }

        if (data.assistant_text) {
          setMessages((prev) => [...prev, { role: 'assistant', content: data.assistant_text }]);
        }
        if (Array.isArray(data.validation_errors) && data.validation_errors.length > 0) {
          setValidationErrors(data.validation_errors);
        }
        if (data.applied && Array.isArray(data.candidate_workflow_steps) && onStepsApplied) {
          onStepsApplied(data.candidate_workflow_steps);
        }
        setLastMeta(data.meta || null);
      } catch (err) {
        if (!isMounted.current) return;
        setError(err.message || 'AI request failed');
      } finally {
        if (!isMounted.current) return;
        setLoading(false);
      }
    },
    [workflowId, workflowSteps, messages, onStepsApplied, isMounted]
  );

  return {
    messages,
    sendMessage,
    loading,
    error,
    validationErrors,
    lastMeta,
    clearPersistedState,
  };
}
